import { addDays, differenceInCalendarDays, endOfDay, isWeekend, startOfDay } from 'date-fns'
import type { CycleDocument } from '@/lib/db/rxdb/schemas/cycle'
import type { CycleWorkItemDocument } from '@/lib/db/rxdb/schemas/cycleWorkItem'

/** Mon–Fri days in [from, to), by calendar day. */
export function workingDaysBetween(from: number, to: number): number {
  let count = 0
  for (let day = startOfDay(from); day.getTime() < startOfDay(to).getTime(); day = addDays(day, 1)) {
    if (!isWeekend(day)) count += 1
  }
  return count
}

export type BurnUpPoint = { day: number; scope: number; done: number }

export type CycleProgress = {
  scope: number
  done: number
  /** Work items added more than a day after the cycle started. */
  addedMidCycle: number
  /** 1-based working day of the cycle today (clamped to the cycle). */
  workingDay: number
  totalWorkingDays: number
  workingDaysLeft: number
  /** One point per calendar day from the start through today (or the end). */
  series: Array<BurnUpPoint>
  totalDays: number
  /** Tasks behind the straight line from 0 to today's scope; 0 when on track. */
  behindBy: number
}

type ProgressTask = { id: string; statusId: string; updatedOn: number }

/**
 * Burn-up numbers for a cycle (design 8c). Scope over time comes from when
 * each work item was added. Tasks don't record when they were completed, so a
 * closed task counts as done from its last update — close enough for the
 * shape of the chart, and exact once the cycle is closed.
 */
export function computeCycleProgress({
  cycle,
  workItems,
  tasks,
  closedStatusIds,
  now = Date.now(),
}: {
  cycle: Pick<CycleDocument, 'startDate' | 'endDate'>
  workItems: ReadonlyArray<Pick<CycleWorkItemDocument, 'workItemId' | 'addedAt' | 'isDeleted'>>
  tasks: ReadonlyArray<ProgressTask>
  closedStatusIds: ReadonlySet<string>
  now?: number
}): CycleProgress {
  const items = workItems.filter((w) => !w.isDeleted)
  const taskById = new Map(tasks.map((t) => [t.id, t]))
  const scopedTasks = items
    .map((w) => taskById.get(w.workItemId))
    .filter((t): t is ProgressTask => !!t)
  const doneTasks = scopedTasks.filter((t) => closedStatusIds.has(t.statusId))

  const start = startOfDay(cycle.startDate).getTime()
  const totalDays = differenceInCalendarDays(cycle.endDate, cycle.startDate) + 1
  const elapsedDays = Math.min(totalDays, Math.max(0, differenceInCalendarDays(now, start) + 1))

  const series: Array<BurnUpPoint> = []
  for (let day = 0; day < elapsedDays; day++) {
    const dayEnd = endOfDay(addDays(start, day)).getTime()
    series.push({
      day,
      scope: items.filter((w) => w.addedAt <= dayEnd).length,
      done: doneTasks.filter((t) => t.updatedOn <= dayEnd).length,
    })
  }

  const totalWorkingDays = workingDaysBetween(cycle.startDate, addDays(cycle.endDate, 1).getTime())
  const workingDay = Math.min(
    totalWorkingDays,
    Math.max(1, workingDaysBetween(cycle.startDate, now) + (isWeekend(now) ? 0 : 1)),
  )
  const workingDaysLeft = Math.max(
    0,
    workingDaysBetween(Math.max(now, cycle.startDate), addDays(cycle.endDate, 1).getTime()),
  )

  const scope = items.length
  const idealNow = totalDays > 1 ? (scope * (elapsedDays - 1)) / (totalDays - 1) : scope
  const behindBy = Math.max(0, Math.round(idealNow - doneTasks.length))

  return {
    scope,
    done: doneTasks.length,
    addedMidCycle: items.filter((w) => w.addedAt > endOfDay(start).getTime()).length,
    workingDay,
    totalWorkingDays,
    workingDaysLeft,
    series,
    totalDays,
    behindBy,
  }
}

/**
 * "Usual capacity" for planning (design 8b): the range of tasks completed in
 * the last few closed cycles. Null until there's history.
 */
export function computeUsualCapacity(
  completedCycleWorkItems: ReadonlyArray<ReadonlyArray<Pick<CycleWorkItemDocument, 'progressTypeAtClose' | 'isDeleted'>>>,
  lookback = 3,
): { low: number; high: number } | null {
  const recent = completedCycleWorkItems.slice(-lookback)
  if (recent.length === 0) return null
  const doneCounts = recent.map(
    (items) => items.filter((w) => !w.isDeleted && w.progressTypeAtClose === 'CD').length,
  )
  return { low: Math.min(...doneCounts), high: Math.max(...doneCounts) }
}
